/**
 * `/keys` -- one flat table of active keys above a retired-keys table
 * (`659:19979`: keys carry no provider card of their own). `routes.ts`
 * mounts this inside `OpsShell`.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, RotateCw } from 'lucide-react';

import { Page, PageHeader, PageSection } from '../../chrome/page';
import { EmptyState, ErrorState, LoadingState } from '../../chrome/state';
import { DataTable } from '../../chrome/table';
import { Button } from '../../ui/button';
import { Chip } from '../../ui/chip';
import { getLocale } from '../../i18n';
import { days } from '../../i18n/format';
import { formatDate } from '../lib/date';
import { useOpsKeys } from '../api/queries';
import type { OpsKey } from '../api/schemas';
import { operationNameOf } from '../operations/messages';
import { opsTitles } from '../shell/titles';
import { TopbarActions } from '../shell/TopbarActions';
import { KeyIdentity } from './KeyIdentity';
import { NewKeyDialog } from './NewKeyDialog';
import { RenewKeyDialog } from './RenewKeyDialog';
import { RetireKeyDialog } from './RetireKeyDialog';
import { kt, kp } from './messages';

/** Past this many days since the current secret last changed, "última
 *  troca" reads in the destructive colour: an operator scanning the column
 *  for what needs attention shouldn't have to read every date to find it.
 *  No producer names this threshold yet, so it is this screen's own call,
 *  roughly midway between the "fine" and "flagged" examples the frame draws
 *  (38 and 46 days). */
const ROTATION_STALE_DAYS = 45;

function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/** Brand names, the same in every locale. */
const NETWORK_LABELS: Record<string, string> = { instagram: 'Instagram', tiktok: 'TikTok' };

/** Two networks run the same operation under one display name, so the
 *  network is what tells their chips apart. */
function usedByName(operationKey: string): string {
  const [network = ''] = operationKey.split('.');
  const name = operationNameOf(operationKey);
  return network === 'intelligence' || network === 'media'
    ? name
    : `${name} · ${NETWORK_LABELS[network] ?? network}`;
}

function UsedByCell(props: { usedBy: string[] }): ReactNode {
  const count = props.usedBy.length;
  return (
    <Chip tone={count === 0 ? 'outline' : 'neutral'} size="sm">
      {count === 0 ? kt('usedBy.none') : kp('usedBy.count.other', count, { n: count })}
    </Chip>
  );
}

function LastRotatedCell(props: { lastChangedAt: string }): ReactNode {
  const count = daysSince(props.lastChangedAt);
  const stale = count > ROTATION_STALE_DAYS;
  return (
    <span className={stale ? 'text-destructive' : undefined}>
      {kt('lastRotated.relative', { days: days(count, getLocale()) })}
    </span>
  );
}

function activeColumns(
  onRenew: (key: OpsKey) => void,
  onRetire: (keyId: string) => void,
): ColumnDef<OpsKey, unknown>[] {
  return [
    {
      id: 'identity',
      header: () => kt('active.header.name'),
      cell: ({ row }) => <KeyIdentity name={row.original.name} lastFour={row.original.last_four} />,
    },
    {
      id: 'usedBy',
      header: () => kt('active.header.usedBy'),
      cell: ({ row }) => <UsedByCell usedBy={row.original.used_by} />,
    },
    {
      id: 'registered',
      header: () => kt('active.header.registered'),
      cell: ({ row }) => formatDate(row.original.created_at, getLocale()),
    },
    {
      id: 'lastRotated',
      header: () => kt('active.header.lastRotated'),
      cell: ({ row }) => <LastRotatedCell lastChangedAt={row.original.last_changed_at} />,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{kt('active.header.actions')}</span>,
      cell: ({ row }) => {
        const key = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={kt('aria.renew', { name: key.name })}
              onClick={() => onRenew(key)}
            >
              <RotateCw aria-hidden />
              {kt('action.renew')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={kt('aria.retire', { name: key.name })}
              onClick={() => onRetire(key.id)}
            >
              {kt('action.retire')}
            </Button>
          </div>
        );
      },
    },
  ];
}

const retiredColumns: ColumnDef<OpsKey, unknown>[] = [
  {
    id: 'identity',
    header: () => kt('retired.header.name'),
    cell: ({ row }) => <KeyIdentity name={row.original.name} lastFour={row.original.last_four} />,
  },
  {
    id: 'retiredAt',
    header: () => kt('retired.header.retiredAt'),
    cell: ({ row }) =>
      row.original.retired_at !== null ? formatDate(row.original.retired_at, getLocale()) : null,
  },
  {
    id: 'deletedAt',
    // `KeyOut` carries one retirement timestamp, not a separate
    // secret-deletion one: the stored secret is deleted at retirement, so
    // `retired_at` already says when.
    header: () => kt('retired.header.deletedAt'),
    cell: ({ row }) =>
      row.original.retired_at !== null ? formatDate(row.original.retired_at, getLocale()) : null,
  },
];

/** Which dialog is open, and over which key -- one dialog of each kind at a
 *  time, so this is a single value rather than independent booleans that
 *  could disagree with each other. */
type DialogState =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'renew'; key: OpsKey }
  | { kind: 'retire'; keyId: string };

export function Keys(): ReactNode {
  const keysQuery = useOpsKeys();
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  // The element focused right before a dialog opened, restored on close --
  // every dialog here is opened from a plain click handler, never a Radix
  // `Trigger`, so nothing else remembers it.
  const triggerRef = useRef<HTMLElement | null>(null);

  const openDialog = useCallback((next: DialogState) => {
    triggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setDialog(next);
  }, []);
  const closeDialog = useCallback(() => setDialog({ kind: 'none' }), []);
  // Radix's own close-time focus restore runs in the closing dialog's own
  // unmount cleanup, and this screen never uses a `Dialog.Trigger` for
  // Radix to remember -- deferred a tick so this restore lands after that
  // cleanup rather than being clobbered by it.
  useEffect(() => {
    if (dialog.kind !== 'none' || triggerRef.current === null) return;
    const trigger = triggerRef.current;
    triggerRef.current = null;
    const id = setTimeout(() => trigger.focus(), 0);
    return () => clearTimeout(id);
  }, [dialog]);

  const onRenew = useCallback((key: OpsKey) => openDialog({ kind: 'renew', key }), [openDialog]);
  const onRetire = useCallback(
    (keyId: string) => openDialog({ kind: 'retire', keyId }),
    [openDialog],
  );
  // Stable across renders -- `flexRender` treats a bare function as a
  // component type, so a `columns` array rebuilt on every render remounts
  // the whole table body (focus loss) even when nothing it reads changed.
  const columns = useMemo(() => activeColumns(onRenew, onRetire), [onRenew, onRetire]);

  if (keysQuery.status === 'error') {
    return (
      <ErrorState failure={keysQuery.error.failure} onRetry={() => void keysQuery.refetch()} />
    );
  }

  const keys = keysQuery.data;
  const activeKeys = keys?.filter((key) => key.state === 'ACTIVE') ?? [];
  const retiredKeys = keys?.filter((key) => key.state === 'RETIRED') ?? [];

  return (
    <Page>
      <PageHeader title={opsTitles.t('keys')} />
      <TopbarActions>
        <Button type="button" onClick={() => openDialog({ kind: 'new' })}>
          <Plus aria-hidden />
          {kt('action.new')}
        </Button>
      </TopbarActions>
      <PageSection title={kt('active.title')}>
        {keys === undefined ? (
          <LoadingState />
        ) : (
          <DataTable<OpsKey>
            columns={columns}
            data={activeKeys}
            caption={kt('active.title')}
            emptyState={<EmptyState title={kt('active.empty')} />}
          />
        )}
      </PageSection>
      <PageSection title={kt('retired.title')}>
        {keys === undefined ? (
          <LoadingState />
        ) : (
          <DataTable<OpsKey>
            columns={retiredColumns}
            data={retiredKeys}
            caption={kt('retired.title')}
            emptyState={<EmptyState title={kt('retired.empty')} />}
          />
        )}
      </PageSection>
      {dialog.kind === 'new' && (
        <NewKeyDialog open onOpenChange={(open) => !open && closeDialog()} />
      )}
      {dialog.kind === 'renew' && (
        <RenewKeyDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          keyId={dialog.key.id}
          name={dialog.key.name}
          lastFour={dialog.key.last_four}
          usedBy={dialog.key.used_by.map((operationKey) => ({
            operationKey,
            name: usedByName(operationKey),
          }))}
        />
      )}
      {dialog.kind === 'retire' && (
        <RetireKeyDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          keyId={dialog.keyId}
        />
      )}
    </Page>
  );
}
